import type { FocusEvent, KeyboardEvent, RefCallback } from "react";
import { Icon } from "../../icons";
import { OptionVisual } from "./OptionVisual";
import type { SelectOption } from "./selectOption";

/** How the trigger is named, described and validated for assistive technology. */
export interface TriggerAccessibility {
  label?: string;
  labelledBy?: string;
  describedBy?: string;
  invalid?: boolean;
  required?: boolean;
}

interface SelectTriggerProps {
  buttonRef: RefCallback<HTMLButtonElement>;
  id?: string;
  title?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  open: boolean;
  /** Id of the listbox while it is open. */
  controls?: string;
  /** Id of the highlighted option while the trigger keeps the focus. */
  activeDescendant?: string;
  accessibility: TriggerAccessibility;
  className: string;
  selected?: SelectOption;
  placeholder: string;
  onClick: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void;
  onBlur?: (e: FocusEvent<HTMLButtonElement>) => void;
  onFocus?: (e: FocusEvent<HTMLButtonElement>) => void;
}

/** The button styled like an input that shows the selected option and opens the list. */
export function SelectTrigger(props: SelectTriggerProps): React.JSX.Element {
  const { accessibility: a11y, selected } = props;
  return (
    <button
      ref={props.buttonRef}
      type="button"
      role="combobox"
      id={props.id}
      title={props.title}
      disabled={props.disabled}
      autoFocus={props.autoFocus}
      aria-haspopup="listbox"
      aria-expanded={props.open}
      aria-controls={props.controls}
      aria-activedescendant={props.activeDescendant}
      aria-label={a11y.label}
      aria-labelledby={a11y.labelledBy}
      aria-describedby={a11y.describedBy}
      aria-invalid={a11y.invalid || undefined}
      aria-required={a11y.required || undefined}
      className={props.className}
      onClick={props.onClick}
      onKeyDown={props.onKeyDown}
      onBlur={props.onBlur}
      onFocus={props.onFocus}
    >
      {selected ? <OptionVisual option={selected} /> : null}
      <span className={`nb-select-value${selected ? "" : " is-placeholder"}`}>{selected?.label ?? props.placeholder}</span>
      <Icon name="chevronDown" className="nb-select-chevron" aria-hidden="true" />
    </button>
  );
}
